import i18n from "../../../i18n";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, ShieldAlert, Pencil, Mail, MapPin, KeyRound, Loader2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { PageHeader, SearchInput, DataTable, LoadingState } from "../../../components/ui";
import { hrmsGet } from "../../../api/api";

function getTableColumns() {
  return [
    { label: i18n.t("common.employeeId"), key: "id" },
    { label: i18n.t("common.employeeName"), key: "name" },
    { label: i18n.t("common.email"), key: "email" },
    { label: i18n.t("common.location4"), key: "location" },
    { label: i18n.t("common.actions"), key: "actions", className: "text-center w-28" }
  ];
}

export function EmployeeListPage() {
  const navigate = useNavigate();
  useTranslation();
  const tableColumns = useMemo(() => getTableColumns(), []);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [regeneratingPasswordFor, setRegeneratingPasswordFor] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);

  // Debounce search query input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchCount = useCallback(async () => {
    try {
      const res = await hrmsGet("/emp-info/count", "HR01S");
      const unwrapped = res?.data ?? res;
      setTotalCount(Number(unwrapped) || 0);
    } catch (err) {
      console.error("Failed to fetch employee count:", err);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let path = "/emp-info/search";
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        sortBy: "empId",
        sortDir: "ASC"
      });

      const query = debouncedSearchQuery.trim();
      if (query) {
        if (query.includes("@")) {
          path = "/emp-info/search/email";
          params.set("email", query);
          params.set("sortBy", "email");
        } else if (query.toLowerCase().startsWith("loc:") || query.toLowerCase().startsWith("location:")) {
          path = "/emp-info/search/location";
          const loc = query.replace(/^loc(ation)?:/i, "").trim();
          params.set("locationType", "work");
          params.set("location", loc);
          params.set("sortBy", "first_Name");
        } else {
          path = "/emp-info/search/name";
          params.set("searchTerm", query);
          params.set("sortBy", "first_Name");
        }
      }

      let res = await hrmsGet(`${path}?${params.toString()}`, "HR01S");
      let unwrapped = res?.data ?? res;
      let content = unwrapped?.content ?? (Array.isArray(unwrapped) ? unwrapped : []);

      // Fallback: If name search yields 0 results, try searching by location
      if (content.length === 0 && query && !query.includes("@") && !query.toLowerCase().startsWith("loc")) {
        try {
          const locParams = new URLSearchParams({
            page: String(page),
            size: String(size),
            locationType: "work",
            location: query,
            sortBy: "first_Name",
            sortDir: "ASC"
          });
          const locRes = await hrmsGet(`/emp-info/search/location?${locParams.toString()}`, "HR01S");
          const locUnwrapped = locRes?.data ?? locRes;
          const locContent = locUnwrapped?.content ?? (Array.isArray(locUnwrapped) ? locUnwrapped : []);
          if (locContent.length > 0) {
            content = locContent;
            unwrapped = locUnwrapped;
          }
        } catch (_) {
          // Ignore fallback errors and show original result
        }
      }

      setEmployees(content);
      setTotalPages(unwrapped?.totalPages ?? 1);
      setTotalElements(unwrapped?.totalElements ?? content.length);

      // Refresh count on data fetch
      fetchCount();
    } catch (err) {
      console.error("Failed to fetch employees:", err);
      setError(i18n.t("onboarding.failedToLoadEmployeeRecords"));
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, debouncedSearchQuery, fetchCount]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const regeneratePassword = async (empId) => {
    setRegeneratingPasswordFor(empId);
    setPasswordMessage(null);
    try {
      await hrmsGet(`/emp-info/generate-pwd/${encodeURIComponent(empId)}`, "HR01U");
      setPasswordMessage({ type: "success", text: i18n.t("onboarding.messages.passwordRegeneratedSuccessfully") });
    } catch (err) {
      console.error("Failed to regenerate employee password:", err);
      setPasswordMessage({ type: "error", text: i18n.t("onboarding.messages.failedToRegeneratePassword") });
    } finally {
      setRegeneratingPasswordFor(null);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <PageBackLink to="/" />
          <PageHeader
            title={i18n.t("onboarding.employeesWithCount", { count: totalCount })}
            action={
              <Button
                onClick={() => navigate("/hrms-team/onboarding/employees/onboard")}
                className="h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]">
                <UserPlus className="h-4 w-4" />
                <Trans i18nKey="formActions.addEmployee" />
              </Button>
            } />
        </div>
        <div className="bg-white p-4 rounded-2xl border border-white/60 shadow-[0_4px_20px_-10px_rgba(30,51,131,0.15)]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setDebouncedSearchQuery(searchQuery);
              setPage(0);
            }}
            className="w-full max-w-md">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={i18n.t("formActions.searchByName2")}
              className="w-full max-w-full" />
          </form>
        </div>

        {passwordMessage &&
          <div
            role="status"
            className={`rounded-xl border px-4 py-3 text-sm font-medium ${passwordMessage.type === "success" ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}`}>
            {passwordMessage.text}
          </div>}
        {loading ?
          <LoadingState message={i18n.t("onboarding.fetchingEmployeeRecords")} /> :
          error ?
            <div className="p-8 text-center text-sm text-[var(--color-error)] bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)]">
              {error}
            </div> :
            employees.length > 0 ?
              <div className="bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] overflow-hidden">
                <DataTable columns={tableColumns} minWidth="760px">
                  {employees.map((emp) => {
                    const empId = emp.empId ?? emp.employeeId ?? emp.id;
                    const empName = emp.fullName ?? [emp.firstName, emp.lastName].filter(Boolean).join(" ") ?? emp.name ?? i18n.t("common.employee");
                    const empEmail = emp.email ?? emp.emailId ?? emp.email_Id ?? "—";
                    const empLocation = emp.workLocation ?? emp.location ?? emp.work_Location ?? emp.addressLocationArea ?? "—";

                    return (
                      <tr
                        key={empId}
                        className="hover:bg-[var(--color-workspace)]/20 transition-colors">
                        <td className="px-4 py-3.5 font-mono text-xs text-[var(--color-text-secondary)] font-medium">
                          #{empId}
                        </td>
                        <td className="px-4 py-3.5 font-semibold text-foreground">
                          {empName}
                        </td>
                        <td className="px-4 py-3.5 text-[var(--color-text-secondary)] text-sm">
                          {empEmail}
                        </td>
                        <td className="px-4 py-3.5 text-[var(--color-text-secondary)] text-sm">
                          {empLocation}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/hrms-team/onboarding/employees/onboard?edit=${empId}`)}
                              title={i18n.t("formActions.editEmployee")}
                              className="p-1.5 rounded-lg text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => regeneratePassword(empId)}
                              disabled={regeneratingPasswordFor === empId}
                              title={i18n.t("onboarding.regeneratePassword")}
                              aria-label={i18n.t("onboarding.regeneratePasswordFor", { name: empName })}
                              className="p-1.5 rounded-lg text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition disabled:cursor-not-allowed disabled:opacity-50">
                              {regeneratingPasswordFor === empId ?
                                <Loader2 className="h-4 w-4 animate-spin" /> :
                                <KeyRound className="h-4 w-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>);
                  })}
                </DataTable>

                <div className="md:hidden divide-y divide-[var(--color-text-border)]/35">
                  {employees.map((emp) => {
                    const empId = emp.empId ?? emp.employeeId ?? emp.id;
                    const empName = emp.fullName ?? [emp.firstName, emp.lastName].filter(Boolean).join(" ") ?? emp.name ?? i18n.t("common.employee");
                    const empEmail = emp.email ?? emp.emailId ?? emp.email_Id ?? "—";
                    const empLocation = emp.workLocation ?? emp.location ?? emp.work_Location ?? emp.addressLocationArea ?? "—";

                    return (
                      <div
                        key={empId}
                        className="p-4 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-sm text-foreground">{empName}</span>
                          <span className="text-[11px] font-mono text-[var(--color-text-secondary)]">#{empId}</span>
                        </div>
                        <div className="text-xs text-[var(--color-text-secondary)] space-y-1">
                          <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 shrink-0" /> {empEmail}</p>
                          <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 shrink-0" /> {empLocation}</p>
                        </div>
                        <div className="pt-2 flex items-center justify-end gap-2 border-t border-[var(--color-text-border)]/20">
                          <button
                            type="button"
                            onClick={() => navigate(`/hrms-team/onboarding/employees/onboard?edit=${empId}`)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--color-primary)]/10 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20 transition">
                            <Pencil className="h-3.5 w-3.5" />
                            <Trans i18nKey="common.edit" />
                          </button>
                          <button
                            type="button"
                            onClick={() => regeneratePassword(empId)}
                            disabled={regeneratingPasswordFor === empId}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--color-primary)]/10 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20 transition disabled:cursor-not-allowed disabled:opacity-50">
                            {regeneratingPasswordFor === empId ?
                              <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                              <KeyRound className="h-3.5 w-3.5" />}
                            <Trans i18nKey="onboarding.regeneratePassword" />
                          </button>
                        </div>
                      </div>);
                  })}
                </div>

                <div className="px-4 py-3 sm:px-6 border-t border-[var(--color-text-border)]/50 bg-[var(--color-workspace)]/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-[var(--color-text-secondary)] font-medium">
                      <Trans i18nKey="pagination.showing" /> <span className="font-semibold text-foreground">{totalElements === 0 ? 0 : page * size + 1}</span> <Trans i18nKey="common.to" />{" "}
                      <span className="font-semibold text-foreground">
                        {Math.min((page + 1) * size, totalElements)}
                      </span>{" "}
                      <Trans i18nKey="common.of" /> <span className="font-semibold text-foreground">{totalElements}</span> <Trans i18nKey="pagination.entries" />
                    </div>
                    <select
                      value={size}
                      onChange={(e) => {
                        setSize(Number(e.target.value));
                        setPage(0);
                      }}
                      className="h-8 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none">
                      <option value={10}><Trans i18nKey="serviceExplorer.10PerPage" /></option>
                      <option value={20}><Trans i18nKey="serviceExplorer.20PerPage" /></option>
                      <option value={50}><Trans i18nKey="serviceExplorer.50PerPage" /></option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className="px-3 py-1.5 rounded-lg border border-[var(--color-text-border)]/60 bg-white text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/40 disabled:opacity-40 disabled:cursor-not-allowed transition font-semibold">
                      <Trans i18nKey="pagination.previous2" />
                    </button>

                    <span className="text-[var(--color-text-secondary)] font-medium">
                      <Trans i18nKey="pagination.page" /> <span className="font-semibold text-foreground">{page + 1}</span> <Trans i18nKey="common.of" />{" "}
                      <span className="font-semibold text-foreground">{totalPages}</span>
                    </span>

                    <button
                      type="button"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      className="px-3 py-1.5 rounded-lg border border-[var(--color-text-border)]/60 bg-white text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/40 disabled:opacity-40 disabled:cursor-not-allowed transition font-semibold">
                      <Trans i18nKey="pagination.next2" />
                    </button>
                  </div>
                </div>
              </div> :

              <div className="text-center py-12 bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)]">
                <ShieldAlert className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-medium text-[var(--color-text-secondary)]"><Trans i18nKey="serviceExplorer.noEmployeesFoundMatchingTheSearchCriteria" /></p>
              </div>
        }
      </div>
    </AppLayout>);
}
